<script setup lang="ts">
import api from '@/config/axios';
import { filterMenuByPermissions, markMenuItemsActive,
    ordenarMenuExibido, menuItems, processMenuLinks, type MenuItem } from '@/config/menu';
import { useAuthStore } from '@/stores/authStore';
import { useI18n } from 'vue-i18n';
import { useCustomizerStore } from '@/stores/customizer';
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useDisplay } from 'vuetify';
import VerticalSidebar from '../vertical-sidebar/VerticalSidebar.vue';
import NavCollapse from './NavCollapse/Index.vue';
import NavItem from './NavItem/Index.vue';

const customizer = useCustomizerStore();
const authStore = useAuthStore();
const route = useRoute();
const { mdAndUp } = useDisplay();
const permissionsLoaded = ref(false);

// Links úteis carregados da API
const { t } = useI18n();
const menuLinksChildren = ref<MenuItem[]>([]);

// Ref para o container de scroll
const scrollContainerRef = ref<HTMLElement | null>(null);

// Carregar links úteis da API
const loadMenuLinks = async () => {
    try {
        const response = await api.get('/menu-links');
        const linksData = response.data.data;
        // Transformar em estrutura de children do menu
        menuLinksChildren.value = Object.entries(linksData).map(([category, links]) => ({
            title: category,
            icon: 'folder-line-duotone',
            BgColor: 'primary',
            to: '#',
            children: (links as any[]).map((link) => ({
                title: link.link_name,
                to: link.link_url,
                type: 'external' as const,
                BgColor: 'primary'
            }))
        }));
    } catch (error) {
        console.error('Erro ao carregar links úteis:', error);
        menuLinksChildren.value = [];
    }
};

// Carregar permissões ao montar o componente
onMounted(async () => {
    // Garantir que permissões do usuário estão carregadas (userAccessMap)
    await authStore.fetchUserRouteLevelPaths();
    await loadMenuLinks();
    permissionsLoaded.value = true;

    // Adicionar scroll horizontal com roda do mouse
    if (scrollContainerRef.value) {
        scrollContainerRef.value.addEventListener(
            'wheel',
            (e: WheelEvent) => {
                // Permitir scroll horizontal com Shift + Wheel ou quando deltaX é maior
                if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
                    e.preventDefault();
                    scrollContainerRef.value!.scrollLeft += e.deltaY || e.deltaX;
                }
            },
            { passive: false }
        );
    }
});

const isAdminMaster = computed(() => authStore.isAdminMaster);

const sidebarMenu = computed(() => {
    // Aguardar carregamento das permissões antes de filtrar
    if (!permissionsLoaded.value) {
        return [];
    }

    // Processar links dinâmicos do menu (ex: @profile?tab=kanban)
    const samaccountname = authStore.user?.sAMAccountName || authStore.user?.username;
    const processedMenu = processMenuLinks(menuItems, samaccountname);

    // Injetar children dinâmicos no item "Links Úteis"
    const menuWithDynamicLinks = processedMenu.map((item) => {
        if (item.title === 'Links Úteis' && menuLinksChildren.value.length > 0) {
            return {
                ...item,
                children: menuLinksChildren.value
            };
        }
        return item;
    });

    // Filtrar menu por permissões (fonte única: userAccessMap do banco de dados)
    const filteredMenu = filterMenuByPermissions(
        ordenarMenuExibido(menuWithDynamicLinks, (x) => t(x)),
        isAdminMaster.value,
        authStore.userGroups,
        authStore.user?.department || '',
        authStore.user?.company || '',
        undefined,
        undefined,
        authStore.userRouteLevelPaths
    );

    // Marcar itens como ativos baseado na rota atual (usar fullPath para incluir query params)
    return markMenuItemsActive(filteredMenu, route.fullPath);
});
</script>

<template>
    <template v-if="mdAndUp">
        <div class="horizontalMenu bg-containerBg position-relative">
            <div :class="customizer.boxed ? 'maxWidth ' : 'px-6'">
                <div ref="scrollContainerRef" class="bg-surface horizontal-head horizontal-scroll-container">
                    <ul class="gap-1 horizontal-navbar mx-lg-0 mx-3">
                        <!---Menu Loop -->
                        <template v-for="(item, i) in sidebarMenu" :key="i">
                            <li class="navItem" v-if="!item.header">
                                <!---If Has Child -->
                                <NavCollapse :item="item" :level="0" v-if="item.children" />
                                <!---Single Item-->
                                <NavItem :item="item" v-else />
                                <!---End Single Item-->
                            </li>
                        </template>
                    </ul>
                </div>
            </div>
        </div>
    </template>
    <div v-else class="mobile-menu">
        <VerticalSidebar />
    </div>
</template>
<style lang="scss">
.horizontal-scroll-container {
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    /* Firefox - ocultar por padrão */
    cursor: grab;

    &:active {
        cursor: grabbing;
    }

    /* Ocultar scrollbar por padrão no Chrome/Safari */
    &::-webkit-scrollbar {
        height: 0;
        background: transparent;
    }

    /* Mostrar scrollbar no hover */
    &:hover {
        scrollbar-width: thin;
        /* Firefox - mostrar no hover */
        scrollbar-color: rgba(0, 0, 0, 0.3) transparent;

        &::-webkit-scrollbar {
            height: 6px;
        }

        &::-webkit-scrollbar-track {
            background: rgba(0, 0, 0, 0.05);
            border-radius: 3px;
        }

        &::-webkit-scrollbar-thumb {
            background-color: rgba(0, 0, 0, 0.3);
            border-radius: 3px;

            &:hover {
                background-color: rgba(0, 0, 0, 0.5);
            }
        }
    }

    .horizontal-navbar {
        display: flex;
        flex-wrap: nowrap;
        min-width: max-content;
        white-space: nowrap;
    }
}
</style>
